import React, { useState } from 'react';
import { ButtonV2, Flex, FormControl, InputV2, SelectV1, TextArea, TextV2, IconV2, useToast } from '@withjoy/joykit';
import { AdminSidebarComponentProps } from '../JsonComponent.types';
import { Close, Trash, Photo, WarningTriangle } from '@withjoy/joykit/icons';
import { useGoToDeleteModal, useGoToReorderView } from '../hooks/useJsonComponentNavigation';
import { categories } from './ThingsToDo.constants';
import { useFormik } from 'formik';
import { useSaveJsonData, useCreateNewJsonComponent } from '../hooks/useJsonBlobStorageUtils';
import { ThingsToDoConfig } from './ThingsToDo.config';
import { useImageFilePicker } from '@apps/admin/common/hooks/useImageFilePicker';
import { useUpdateWebsitePreview } from '@website-editor/hooks/useUpdateWebsitePreview';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import * as Yup from 'yup';
import { useTranslation } from '@shared/core';
import { useThingToDoCategoryUtils } from '@apps/guest/packages/layout-engine/widgets/Custom/components/ThingToDo/hooks';
import { PhotoUploadPlaceholder, ImagePreview } from './ThingToDo.styles';
import { useWebsiteEditorThingsToDoTelemetry } from '../DefaultThingsToDo/DefaultThingsToDo.telemetry';
import { useEventInfo } from '@shared/utils/eventInfo';
import { PointOfInterest, ThingsToDo } from './ThingsToDo.types';
import { useDeletePageComponentV2Mutation } from '@graphql/generated';
// Initial data for new components
export const INITIAL_DATA: ThingsToDo = {
  lang: 'en',
  location: { city: 'N/A' },
  poi: {
    id: '',
    title: '',
    url: '',
    category: 'attraction',
    description: '',
    tours: [],
  },
};

interface ThingsToDoFormValues {
  title: string;
  url: string;
  category: string;
  personalNote: string;
  customCategory: string;
}

export const ThingsToDoEdit = (props: AdminSidebarComponentProps) => {
  const { t } = useTranslation('websiteEditor');
  const {
    title,
    add,
    edit,
    delete: deleteText,
    category,
    website,
    websitePlaceholder,
    name,
    namePlaceholder,
    personalNote,
    personalNotePlaceholder,
    titleRequired,
    urlInvalid,
    categoryRequired,
    customCategory,
    customCategoryPlaceholder,
    cancel,
    save,
    failedUpdate,
    failedCreate,
  } = t('customPage', 'thingsToDo');

  const telemetry = useWebsiteEditorThingsToDoTelemetry();
  const eventInfo = useEventInfo();
  const { goToReorderView } = useGoToReorderView();
  const { goToDeleteModal } = useGoToDeleteModal();
  const { saveJsonData } = useSaveJsonData();
  const { createNewJsonComponent } = useCreateNewJsonComponent(props.eventHandle);
  const [deletePageComponentV2] = useDeletePageComponentV2Mutation();
  const { updateWebsitePreview } = useUpdateWebsitePreview();
  const { setRefetchQueries } = useCustomComponentStateContext();
  const thingsToDoContent: ThingsToDo | null = props.componentData.containerContent.content;
  const { openFilepicker, updatedImage, imageName, uploadImage } = useImageFilePicker({
    eventId: props.componentData.containerContent.eventId,
    initialImageUrl: props.componentData.containerContent.content.poi?.image?.url || undefined,
  });
  const { getCategoryIcon } = useThingToDoCategoryUtils();
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const { isEditMode } = props;

  const validationSchema = Yup.object({
    title: Yup.string().required(titleRequired()),
    url: Yup.string().test('url-or-empty', urlInvalid(), value => !value || Yup.string().url().isValidSync(value)),
    category: Yup.string().required(categoryRequired()),
    personalNote: Yup.string().required(),
    customCategory: Yup.string().when('category', {
      is: (val: string) => val === 'custom',
      then: Yup.string().required(categoryRequired()),
      otherwise: Yup.string().notRequired(),
    }),
  });

  // Determine if saved category is custom (not in predefined list)
  const savedCategory = thingsToDoContent?.poi.category || 'attraction';
  const isCustomCategory = !categories.find(c => c.value === savedCategory);
  const initialCategory = isCustomCategory ? 'custom' : savedCategory;
  const initialCustomCategory = isCustomCategory ? savedCategory : '';

  const initialValues: ThingsToDoFormValues = {
    title: thingsToDoContent?.poi.title || '',
    url: thingsToDoContent?.poi.url || '',
    category: initialCategory,
    personalNote: thingsToDoContent?.poi.description || '',
    customCategory: initialCustomCategory,
  };
  const isOwnContainer = props.componentData.containerContent.eventId === eventInfo.eventInfo?.eventId;

  const isGetYourGuideUrl = initialValues.url?.toLowerCase().includes('getyourguide.com') || false;

  const formik = useFormik<ThingsToDoFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async values => {
      setLoading(true);
      let uploadedImage = undefined;
      if (updatedImage && imageName) {
        uploadedImage = await uploadImage();
      }
      const poiData: PointOfInterest & { category: string; description: string } = {
        ...thingsToDoContent?.poi,
        id: thingsToDoContent?.poi.id || values.title + Date.now(),
        title: values.title,
        url: values.url,
        category: values.category === 'custom' ? values.customCategory : values.category,
        description: values.personalNote,
        image: uploadedImage ? { url: uploadedImage.url, id: uploadedImage.id } : thingsToDoContent?.poi.image,
      };

      const componentData = {
        lang: 'en',
        location: { city: 'N/A' },
        ...thingsToDoContent,
        poi: poiData,
      };

      if (isEditMode && isOwnContainer) {
        try {
          await saveJsonData({
            id: props.componentData.containerContent.id,
            content: componentData,
            type: ThingsToDoConfig.componentType,
            schemaUrl: ThingsToDoConfig.schemaUrl,
          });
          telemetry.updateThingsToDoClicked(props.pageSlug, {
            title: componentData.poi.title,
            url: componentData.poi.url,
            category: componentData.poi.category,
            personalNote: componentData.poi.description,
            image: componentData.poi.image?.url || undefined,
          });
          // Request a refetch of the components list to update the left panel
          setRefetchQueries(['PageContainerBySlug']);
          updateWebsitePreview();
          setLoading(false);
          goToReorderView();
        } catch {
          setLoading(false);
          toast(`${failedUpdate()}`, { icon: <WarningTriangle />, duration: 5000 });
        }
      } else {
        try {
          const result = await createNewJsonComponent({
            schemaURL: ThingsToDoConfig.schemaUrl,
            type: ThingsToDoConfig.componentType,
            initialData: componentData,
            containerId: props.containerId,
            sortOrder: props.componentData.sortOrder,
          });
          if (props.componentData.id) {
            await deletePageComponentV2({
              variables: {
                payload: {
                  id: props.componentData.id,
                },
              },
            });
          }
          if (result?.containerData?.data) {
            telemetry.saveThingsToDoClicked(props.pageSlug, {
              title: componentData.poi.title,
              url: componentData.poi.url,
              category: componentData.poi.category,
              personalNote: componentData.poi.description,
              image: componentData.poi.image?.url || undefined,
            });
            // Request a refetch of the components list to update the left panel
            setRefetchQueries(['PageContainerBySlug']);
            updateWebsitePreview();
            setLoading(false);
            goToReorderView();
          }
        } catch {
          setLoading(false);
          toast(`${failedCreate()}`, { icon: <WarningTriangle />, duration: 5000 });
        }
      }
    },
  });

  return (
    <>
      <Flex
        flexDirection="column"
        paddingX={8}
        paddingY={6}
        position="relative"
        overflow="auto"
        height="100%"
      >
        <Flex
          cursor="pointer"
          onClick={goToReorderView}
          position="absolute"
          top={6}
          right={6}
          border="1px solid"
          borderColor="mono3"
          padding={1}
          borderRadius={'50%'}
        >
          <Close size={24} />
        </Flex>
        <Flex
          justifyContent="center"
          alignItems="center"
          marginBottom={6}
        >
          <TextV2 typographyVariant="hed6">
            {isEditMode ? edit() : add()} {title()}
          </TextV2>
        </Flex>
        <form onSubmit={formik.handleSubmit}>
          <Flex
            flexDirection="column"
            gap={6}
          >
            <FormControl
              label={category()}
              isInvalid={formik.touched.category && !!formik.errors.category}
              error={formik.errors.category}
            >
              <SelectV1
                autoComplete="off"
                searchable={false}
                iconLeft={<IconV2>{getCategoryIcon(formik.values.category || 'attraction')}</IconV2>}
                options={categories}
                value={categories.find(c => c.value === formik.values.category)}
                onChange={option => formik.setFieldValue('category', option?.value || 'attraction')}
              />
            </FormControl>
            {formik.values.category === 'custom' && (
              <FormControl
                label={customCategory()}
                isInvalid={formik.touched.customCategory && !!formik.errors.customCategory}
                error={formik.errors.customCategory}
              >
                <InputV2
                  placeholder={customCategoryPlaceholder()}
                  value={formik.values.customCategory}
                  onChange={e => formik.setFieldValue('customCategory', e.target.value)}
                />
              </FormControl>
            )}
            {!isGetYourGuideUrl && (
              <FormControl
                label={website()}
                isInvalid={formik.touched.url && !!formik.errors.url}
                error={formik.errors.url}
              >
                <InputV2
                  placeholder={websitePlaceholder()}
                  value={formik.values.url}
                  onChange={e => formik.setFieldValue('url', e.target.value)}
                />
              </FormControl>
            )}
            <FormControl
              label={name()}
              isInvalid={formik.touched.title && !!formik.errors.title}
              error={formik.errors.title}
            >
              <InputV2
                placeholder={namePlaceholder()}
                value={formik.values.title}
                onChange={e => formik.setFieldValue('title', e.target.value)}
              />
            </FormControl>
            <PhotoUploadPlaceholder onClick={openFilepicker}>
              {updatedImage ? (
                <ImagePreview src={updatedImage} />
              ) : (
                <Photo
                  size={48}
                  color={'mono8'}
                />
              )}
            </PhotoUploadPlaceholder>
            <FormControl
              label={personalNote()}
              isInvalid={formik.touched.personalNote && !!formik.errors.personalNote}
              error={formik.errors.personalNote}
            >
              <TextArea
                value={formik.values.personalNote}
                placeholder={personalNotePlaceholder()}
                minRows={4}
                onChange={e => formik.setFieldValue('personalNote', e.target.value)}
              />
            </FormControl>
            {isEditMode && (
              <Flex
                justifyContent="center"
                alignItems="center"
              >
                <Flex
                  gap={3}
                  cursor="pointer"
                  alignItems="center"
                  onClick={() => goToDeleteModal(props.componentData)}
                >
                  <Trash
                    size={24}
                    color="negative6"
                  />
                  <TextV2
                    typographyVariant="button2"
                    color="negative6"
                  >
                    {deleteText()}
                  </TextV2>
                </Flex>
              </Flex>
            )}
            <Flex
              justifyContent="space-around"
              alignItems="center"
              marginTop={9}
            >
              <ButtonV2
                flex={1}
                shape="rounded"
                variant="ghost"
                intent="neutral"
                onClick={goToReorderView}
              >
                {cancel()}
              </ButtonV2>
              <ButtonV2
                type="submit"
                flex={1}
                shape="rounded"
                intent="neutral"
                loading={loading}
              >
                {save()}
              </ButtonV2>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </>
  );
};
